import { useCallback, useEffect, useRef, useState } from 'react';
import type { SettlementView } from '@cuito/types';
import { Button } from '@cuito/ui';
import { consignmentsApi } from '../api/consignments';
import { SETTLEMENT_PAGE_SIZE, settlementsApi } from '../api/settlements';
import { stockApi, type TransferView } from '../api/stock';
import CarryOverDialog from '../components/settlement/CarryOverDialog';
import GenerateSettlementDialog from '../components/settlement/GenerateSettlementDialog';
import SettleDialog from '../components/settlement/SettleDialog';
import SettlementLinesTable from '../components/settlement/SettlementLinesTable';
import SettlementSummaryCards from '../components/settlement/SettlementSummaryCards';
import {
  LOAD_FAILED_TEXT,
  settlementDirectionClass,
  settlementDirectionText,
  settlementStatusClass,
  settlementStatusText,
} from '../components/settlement/settlementLabels';
import { readServerMessage } from '../lib/apiError';
import { useAuthStore } from '../stores/authStore';

/**
 * 结算页（契约 §15；UI 规范 554 行）。
 *
 * 三件必须坚持的事：
 *   ① **列表行不挂四个金额的 testid**：那四个 testid 归 `SettlementSummaryCards`（详情与预览各一份实例）；
 *      行上重复挂会让验收脚本的定位变成「第一个」这种脆弱判据；
 *   ② **写操作按权限隐藏**（`settlement:write`）：生成 / 标记已结算 / 选择库存去向三个按钮；
 *   ③ **结算成功后重取**（列表 + 详情）：`POST /settlements/:id/settle` 的响应只有五个键，
 *      不含四个金额 ⇒ 拿响应体当详情会让金额显示 `undefined`。
 *
 * 金额一律显示服务端字符串（`SettlementSummaryCards` 不做任何减法）；方向与状态文案的唯一出处是
 * `settlementLabels.ts`（`正向结算` / `反向结算` / `待结算` / `已结算`）。
 */
interface Notice {
  text: string;
}

export default function SettlementsPage() {
  const canRead = useAuthStore((s) => s.hasPermission('settlement:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('settlement:write'));
  const myTenantId = useAuthStore((s) => s.user?.tenant.id ?? '');

  const [settlements, setSettlements] = useState<SettlementView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const [selected, setSelected] = useState<SettlementView | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [counterparties, setCounterparties] = useState<Array<{ id: string; name: string }>>([]);
  const [events, setEvents] = useState<Array<{ id: string; name: string }>>([]);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [transfers, setTransfers] = useState<TransferView[]>([]);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [carryOpen, setCarryOpen] = useState(false);

  const listSeq = useRef(0);
  const detailSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    if (!canRead) return;
    listSeq.current += 1;
    const token = listSeq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await settlementsApi.list({ page: 1, pageSize: SETTLEMENT_PAGE_SIZE });
      if (token !== listSeq.current) return;
      setSettlements(result.items);
    } catch (e) {
      if (token !== listSeq.current) return;
      setError(readServerMessage(e) ?? LOAD_FAILED_TEXT);
    } finally {
      if (token === listSeq.current) setLoading(false);
    }
  }, [canRead]);

  const loadDetail = useCallback(async (id: string): Promise<void> => {
    detailSeq.current += 1;
    const token = detailSeq.current;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const row = await settlementsApi.detail(id);
      if (token !== detailSeq.current) return;
      setSelected(row);
    } catch (e) {
      if (token !== detailSeq.current) return;
      setDetailError(readServerMessage(e) ?? LOAD_FAILED_TEXT);
    } finally {
      if (token === detailSeq.current) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * 弹窗的候选项（一次性取，失败 ⇒ 空列表 + 由弹窗给出服务端错误）：
   *   · 对方社团 = **我的受托台账里的货主**（与 A-P7-16 的服务端判据同一个集合）+ 列表里我作为付款方的对方；
   *   · 展会 = `GET /locations?type=EVENT`（`nextEventId` 的取值集合；生成弹窗**不做**展会选择器，A-P7-51）；
   *   · 仓库 = `GET /warehouses`（`warehouse.location.id` 才是地点 id）；
   *   · 可选取向的调拨单 = `GET /stock/transfers?status=RECEIVED`（**待裁决 D-7**）。
   */
  useEffect(() => {
    if (!canRead) return;
    void (async () => {
      try {
        const inbound = await consignmentsApi.inbound({ page: 1, pageSize: 100 });
        setCounterparties(inbound.items.map((item) => ({ id: item.ownerTenant.id, name: item.ownerTenant.name })));
      } catch {
        setCounterparties([]);
      }
      try {
        const locations = await stockApi.listLocations({ type: 'EVENT' });
        setEvents(locations.map((location) => ({ id: location.id, name: location.name })));
      } catch {
        setEvents([]);
      }
      try {
        const rows = await stockApi.listWarehouses();
        setWarehouses(
          rows.flatMap((warehouse) =>
            warehouse.location === null ? [] : [{ id: warehouse.location.id, name: warehouse.name }],
          ),
        );
      } catch {
        setWarehouses([]);
      }
      try {
        const result = await stockApi.listTransfers({ status: 'RECEIVED' });
        setTransfers(result.items);
      } catch {
        setTransfers([]);
      }
    })();
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="max-w-7xl" data-testid="settlements-page">
        <p className="font-bold text-red-600" data-testid="settlements-error">
          {LOAD_FAILED_TEXT}
        </p>
      </div>
    );
  }

  const payerOptions = settlements
    .filter((row) => row.payerTenant.id === myTenantId)
    .map((row) => row.payeeTenant);
  const mergedCounterparties = [...counterparties, ...payerOptions].filter(
    (tenant, index, all) => all.findIndex((other) => other.id === tenant.id) === index,
  );

  return (
    <div className="max-w-7xl" data-testid="settlements-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">结算</h1>
        {canWrite && (
          <Button data-testid="settlement-generate-open" onClick={() => setGenerateOpen(true)}>
            生成结算单
          </Button>
        )}
      </div>

      {notice !== null && (
        <p data-testid="settlements-notice" className="mb-3 rounded-lg border-2 border-black bg-gray-50 p-2 text-sm">
          {notice.text}
        </p>
      )}

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error !== null && (
        <p className="font-bold text-red-600" data-testid="settlements-error">
          {error}
        </p>
      )}
      {!loading && error === null && settlements.length === 0 && <p className="text-gray-500">暂无结算单</p>}
      {!loading && error === null && settlements.length > 0 && (
        <table className="mb-6 w-full border-collapse text-sm">
          <thead>
            <tr className="border-b-2 border-black text-left">
              <th className="py-2">单号</th>
              <th className="py-2">方向</th>
              <th className="py-2">状态</th>
              <th className="py-2">货主</th>
              <th className="py-2">持货人</th>
              <th className="py-2">净额</th>
            </tr>
          </thead>
          <tbody>
            {settlements.map((row) => (
              <tr
                key={row.id}
                data-testid="settlement-row"
                data-settlement-code={row.code}
                data-direction={row.direction}
                data-status={row.status}
                data-net={row.netSettlement}
                onClick={() => void loadDetail(row.id)}
                className="cursor-pointer border-b border-gray-200 hover:bg-gray-50"
              >
                <td className="py-2 font-bold">{row.code}</td>
                <td className="py-2">
                  <span
                    data-testid="settlement-direction"
                    className={`rounded border px-1.5 py-0.5 text-xs font-bold ${settlementDirectionClass(row.direction)}`}
                  >
                    {settlementDirectionText(row.direction)}
                  </span>
                </td>
                <td className="py-2">
                  <span
                    data-testid="settlement-status"
                    className={`rounded border px-1.5 py-0.5 text-xs font-bold ${settlementStatusClass(row.status)}`}
                  >
                    {settlementStatusText(row.status)}
                  </span>
                </td>
                <td className="py-2">{row.payeeTenant.name}</td>
                <td className="py-2">{row.payerTenant.name}</td>
                <td className="py-2">{row.netSettlement}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected !== null && (
        <section className="rounded-lg border-2 border-black p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold">
              {selected.code} · {settlementDirectionText(selected.direction)}
            </h2>
            <div className="flex gap-2">
              {canWrite && selected.status === 'PENDING' && (
                <Button data-testid="settlement-settle-open" onClick={() => setSettleOpen(true)}>
                  标记已结算
                </Button>
              )}
              {canWrite && (
                <Button data-testid="carry-over-open" variant="secondary" onClick={() => setCarryOpen(true)}>
                  选择库存去向
                </Button>
              )}
            </div>
          </div>

          <SettlementSummaryCards
            totalSales={selected.totalSales}
            refundDeduction={selected.refundDeduction}
            commission={selected.commission}
            netSettlement={selected.netSettlement}
          />
          <SettlementLinesTable lines={selected.lines ?? []} loading={detailLoading} error={detailError} />
        </section>
      )}

      {generateOpen && (
        <GenerateSettlementDialog
          counterparties={mergedCounterparties}
          onClose={() => setGenerateOpen(false)}
          onGenerated={(created) => {
            setGenerateOpen(false);
            setNotice({ text: `已生成结算单 ${created.code}` });
            void load();
            void loadDetail(created.id);
          }}
        />
      )}

      {settleOpen && selected !== null && (
        <SettleDialog
          settlement={selected}
          onClose={() => setSettleOpen(false)}
          onSettled={() => {
            setSettleOpen(false);
            setNotice({ text: `已标记结算 ${selected.code}` });
            void load();
            void loadDetail(selected.id);
          }}
        />
      )}

      {carryOpen && (
        <CarryOverDialog
          transfers={transfers}
          warehouses={warehouses}
          events={events}
          onClose={() => setCarryOpen(false)}
          onDone={(result) => {
            setCarryOpen(false);
            setNotice({ text: `已生成子调拨单 ${result.code}` });
          }}
        />
      )}
    </div>
  );
}
